/**
 * As três abas da tela Integrações (história 11.2, FR-82, AD-30; abas desde o realinhamento ao
 * protótipo v2 de 28/09/2026): **WhatsApp**, **Carga Máquina (ERP)** e **E-mail**. Cada aba é
 * uma grade de cartões lado a lado.
 *
 * **Quem vê o quê.** A tela é de todos (`acessar_integracoes`), e o que cada um encontra é recortado
 * pelo serviço:
 *
 * * **WhatsApp** — três cartões: a conexão do **próprio** número (`MeuNumero`, 8.1, FR-71) para
 *   todo mundo — é daqui, e não mais de `/whatsapp`, que cada um conecta o seu chip —; os
 *   **números da equipe** (`NumerosDaEquipe`), só para o Admin, que é quem `GET /numeros` atende;
 *   e o **provedor (uazapi)** — o token de administrador e a URL do webhook —, só para quem tem
 *   `editar_credenciais_de_integracao`.
 * * **Carga Máquina** — o token da API e o segredo do conector, só com a permissão de credenciais;
 *   os recebimentos do conector (5.2) como sempre, montados só para quem pode lê-los (escopo
 *   `todas` — o componente devolve `null` aos demais).
 * * **E-mail** — a conta IMAP e os destinatários do aviso de RAT, editáveis, e o SMTP como **estado
 *   derivado**, só leitura: a conta SMTP é compartilhada com a `identidade` e mora só no ambiente
 *   (AD-30 emendado em 24/09/2026). Tudo só com a permissão de credenciais.
 *
 * **A URL do webhook é derivada, e não gravada.** O provedor precisa saber para onde avisar, e o
 * endereço é sempre o gateway desta origem mais a rota do `comunicacao`
 * (`/api/comunicacao/webhook/uazapi`, com o prefixo que o Caddy remove). Ela é só leitura, com
 * "Copiar" — a pessoa a cola no painel do provedor.
 *
 * **O que o protótipo tem e o serviço não oferece fica de fora**, e a ausência é decisão: a "URL
 * do servidor" do uazapi é a variável de ambiente `UAZAPI_URL_BASE` e nenhuma rota a devolve nem
 * troca; "Testar conexão" e "Gerar novo segredo" não têm chamada no serviço.
 *
 * **Para quem não tem a permissão, nenhum detalhe de credencial existe na tela** — nem estado, nem
 * últimos caracteres, nem formulário. A consulta nem sai (`habilitada: false`): o serviço
 * responderia `credenciais: null`, e não há nada a mostrar. Esconder é conveniência; quem recorta é
 * o serviço, que devolve o bloco `null` e recusa o `PUT` com `403`.
 *
 * **O prazo do ERP não está aqui, e a ausência é decisão** (FR-82, AD-6): ele é a variável de
 * ambiente `PRAZO_DO_ERP_EM_SEGUNDOS`, e não tela. Não há também botão de "testar conexão": a
 * chamada de teste ao provedor não foi pedida.
 */

import { type FormEvent, type ReactNode, useId, useState } from 'react'

import {
  type ContaImap,
  type CredenciaisDaComunicacao,
  useIntegracoesDaComunicacao,
  useTrocarCredencialDaComunicacao,
} from '@/modulos/comunicacao/api'
import { MeuNumero } from '@/modulos/comunicacao/MeuNumero'
import type { Sessao } from '@/modulos/identidade/api'
import { useSessao } from '@/modulos/identidade/sessao'
import { RecebimentosDoConector } from '@/modulos/integracao-erp/RecebimentosDoConector'
import { anunciar } from '@/modulos/negocios/avisos'
import { IconeDeCopiar, copiarParaAreaDeTransferencia } from '@/modulos/negocios/copiar'
import {
  type CredenciaisDoErp,
  useIntegracoesDoErp,
  useTrocarCredencialDoErp,
} from '@/modulos/integracao-erp/api'
import { useEhCelular } from '@/shell/Shell'
import { ehDeGestao } from '@/shell/navegacao'
import {
  CARREGANDO_AS_CREDENCIAIS,
  ErroDaLeitura,
  EstadoDaCredencialNaTela,
  SEM_CREDENCIAIS_PARA_ESTE_PERFIL,
  TrocaDeSegredo,
  dataBrasileira,
  mensagemDe,
} from './Credencial'
import { NumerosDaEquipe } from './NumerosDaEquipe'
import './integracoes.css'

/** A permissão que abre as credenciais — conveniência de tela; quem recorta é o serviço. */
export const PERMISSAO_DE_CREDENCIAIS = 'editar_credenciais_de_integracao'

/** Se a sessão tem a permissão de ver e editar credenciais. */
export function usePodeEditarCredenciais(): boolean {
  const { data: sessao } = useSessao()
  return sessao !== undefined && PERMISSAO_DE_CREDENCIAIS in sessao.permissoes
}

/** A grade de uma aba: os cartões lado a lado no computador, empilhados no celular. */
function Grade({ children }: { children: ReactNode }) {
  return <div className="integracoes__grade">{children}</div>
}

/** Um cartão com cabeçalho (título + ajuda) e corpo, nomeado para o leitor de tela. */
function Cartao({
  titulo,
  ajuda,
  children,
}: {
  titulo: string
  ajuda: string
  children: ReactNode
}) {
  const id = useId()
  return (
    <section className="integracoes__secao" aria-labelledby={id}>
      <div className="integracoes__cartao">
        <div className="integracoes__cabecalho">
          <h3 className="integracoes__titulo" id={id}>
            {titulo}
          </h3>
          <p className="integracoes__ajuda">{ajuda}</p>
        </div>
        <div className="integracoes__corpo">{children}</div>
      </div>
    </section>
  )
}

/* --- WhatsApp -------------------------------------------------------------------------- */

/** A rota do webhook no `comunicacao`, atrás do prefixo que o gateway remove (AD-24). */
const CAMINHO_DO_WEBHOOK_DO_UAZAPI = '/api/comunicacao/webhook/uazapi'

/** A URL que se cola no painel do provedor: a origem desta página mais a rota do webhook. */
export function urlDoWebhookDoUazapi(): string {
  return `${window.location.origin}${CAMINHO_DO_WEBHOOK_DO_UAZAPI}`
}

export const URL_DO_WEBHOOK_COPIADA = 'URL do webhook copiada.'
export const URL_DO_WEBHOOK_NAO_COPIADA =
  'Não foi possível copiar. Selecione a URL e copie à mão.'
const AJUDA_DO_TOKEN_DE_ADMINISTRADOR =
  'Usado só para criar a instância de cada número. O token de cada instância nasce no pareamento.'

function UrlDoWebhook() {
  const id = useId()
  const url = urlDoWebhookDoUazapi()
  return (
    <div className="integracoes__bloco">
      <label className="integracoes__rotulo" htmlFor={id}>
        URL do webhook (copie no painel do provedor)
      </label>
      <div className="integracoes__linha">
        <input
          id={id}
          className="integracoes__campo integracoes__campo--mono"
          readOnly
          value={url}
          onFocus={(evento) => {
            evento.target.select()
          }}
        />
        <button
          type="button"
          className="integracoes__copiar"
          onClick={() => {
            void copiarParaAreaDeTransferencia(url).then((copiou) => {
              anunciar(copiou ? URL_DO_WEBHOOK_COPIADA : URL_DO_WEBHOOK_NAO_COPIADA)
            })
          }}
        >
          <IconeDeCopiar />
          Copiar
        </button>
      </div>
    </div>
  )
}

function ProvedorDoWhatsapp() {
  const consulta = useIntegracoesDaComunicacao()
  const troca = useTrocarCredencialDaComunicacao()

  return (
    <Cartao
      titulo="Provedor (uazapi)"
      ajuda="As chaves ficam no servidor; aqui só quem é Admin vê e troca."
    >
      <ConteudoDasCredenciais consulta={consulta}>
        {(credenciais: CredenciaisDaComunicacao) => (
          <>
            <div className="integracoes__bloco">
              <h4 className="integracoes__subtitulo">Token de administrador</h4>
              <EstadoDaCredencialNaTela estado={credenciais.uazapi} />
              <TrocaDeSegredo
                rotulo="Novo token de administrador"
                ajuda={AJUDA_DO_TOKEN_DE_ADMINISTRADOR}
                comOlho
                gravacao={troca}
                aoTrocar={(valor, aoGravar) => {
                  troca.mutate(
                    { credencial: 'uazapi', tokenDeAdmin: valor },
                    { onSuccess: aoGravar },
                  )
                }}
              />
            </div>
            <UrlDoWebhook />
          </>
        )}
      </ConteudoDasCredenciais>
    </Cartao>
  )
}

/** A aba WhatsApp: o próprio número, os números da equipe e o provedor, lado a lado. */
export function AbaDoWhatsapp() {
  const podeEditar = usePodeEditarCredenciais()
  return (
    <Grade>
      <MeuNumero />
      <NumerosDaEquipe />
      {podeEditar ? <ProvedorDoWhatsapp /> : null}
    </Grade>
  )
}

/* --- Carga Máquina -------------------------------------------------------------------- */

function CredenciaisDoCargaMaquina() {
  const consulta = useIntegracoesDoErp()
  const trocaDoToken = useTrocarCredencialDoErp()
  const trocaDoSegredo = useTrocarCredencialDoErp()

  return (
    <Cartao
      titulo="Credenciais do Carga Máquina"
      ajuda="O token da API da Silcom e o segredo que o conector apresenta ao CRM. Trocar vale na próxima chamada, sem reiniciar nada."
    >
      <ConteudoDasCredenciais consulta={consulta}>
        {(credenciais: CredenciaisDoErp) => (
          <>
            <div className="integracoes__bloco">
              <h4 className="integracoes__subtitulo">Token da API</h4>
              <EstadoDaCredencialNaTela estado={credenciais.token_da_api} />
              <TrocaDeSegredo
                rotulo="Novo token da API"
                gravacao={trocaDoToken}
                aoTrocar={(valor, aoGravar) => {
                  trocaDoSegredo.reset()
                  trocaDoToken.mutate(
                    { credencial: 'token_da_api', valor },
                    { onSuccess: aoGravar },
                  )
                }}
              />
            </div>
            <div className="integracoes__bloco">
              <h4 className="integracoes__subtitulo">Segredo do conector</h4>
              <EstadoDaCredencialNaTela estado={credenciais.segredo_do_conector} />
              <TrocaDeSegredo
                rotulo="Novo segredo do conector"
                gravacao={trocaDoSegredo}
                aoTrocar={(valor, aoGravar) => {
                  trocaDoToken.reset()
                  trocaDoSegredo.mutate(
                    { credencial: 'segredo_do_conector', valor },
                    { onSuccess: aoGravar },
                  )
                }}
              />
            </div>
          </>
        )}
      </ConteudoDasCredenciais>
    </Cartao>
  )
}

/**
 * Se a aba Carga Máquina tem alguma coisa para esta sessão: credencial (com a permissão) ou
 * recebimentos (gestão). Sem nada, a aba nem aparece na barra — esconder é conveniência; quem
 * recorta é o serviço.
 */
export function temAbaDoCargaMaquina(
  sessao: Pick<Sessao, 'permissoes' | 'escopo_de_carteira'> | undefined,
): boolean {
  if (sessao === undefined) {
    return false
  }
  return PERMISSAO_DE_CREDENCIAIS in sessao.permissoes || ehDeGestao(sessao.escopo_de_carteira)
}

/** A aba Carga Máquina (ERP): as credenciais e os recebimentos do conector, lado a lado. */
export function AbaDoCargaMaquina() {
  const podeEditar = usePodeEditarCredenciais()
  return (
    <Grade>
      {podeEditar ? <CredenciaisDoCargaMaquina /> : null}
      <RecebimentosDoConector />
    </Grade>
  )
}

/* --- E-mail --------------------------------------------------------------------------- */

const SEGURANCAS: { valor: ContaImap['seguranca']; rotulo: string }[] = [
  { valor: 'ssl', rotulo: 'SSL (porta 993)' },
  { valor: 'starttls', rotulo: 'STARTTLS (porta 143)' },
  { valor: 'nenhuma', rotulo: 'Nenhuma' },
]

export const FALTA_O_SERVIDOR = 'Informe o servidor IMAP.'
export const PORTA_INVALIDA = 'A porta é um número entre 1 e 65535.'
export const CONTA_SALVA = 'Conta IMAP salva.'
export const REMOVER_A_SENHA = 'Remover senha'
export const SENHA_REMOVIDA = 'Senha IMAP removida.'
export const FALTA_DESTINATARIO = 'Informe ao menos um destinatário.'
export const DESTINATARIOS_SALVOS = 'Destinatários salvos.'

function ContaImapNaTela({ credenciais }: { credenciais: CredenciaisDaComunicacao }) {
  const imap = credenciais.imap
  const troca = useTrocarCredencialDaComunicacao()
  const [removida, setRemovida] = useState(false)

  return (
    <div className="integracoes__bloco">
      <h4 className="integracoes__subtitulo">Conta IMAP</h4>
      <p className="integracoes__estado">
        <span
          className="integracoes__selo"
          data-situacao={imap.configurada ? 'configurada' : 'ausente'}
        >
          {imap.configurada ? 'Configurado' : 'Não configurado'}
        </span>
        {imap.atualizada_em ? (
          <>
            {' · atualizado em '}
            <span className="numero">{dataBrasileira(imap.atualizada_em)}</span>
          </>
        ) : null}
      </p>
      <p className="integracoes__ajuda">Senha:</p>
      <EstadoDaCredencialNaTela estado={imap.senha} />
      {/*
       * O formulário remonta quando o estado do servidor muda (`key` por `atualizada_em`): o que
       * outra aba, outra pessoa ou a semente gravou aparece nos campos, em vez de o rascunho velho
       * ficar por cima. A mutação e as mensagens ficam aqui fora, e sobrevivem à remontagem.
       */}
      <FormularioDaContaImap
        key={imap.atualizada_em ?? 'sem-conta'}
        imap={imap}
        salvando={troca.isPending}
        aoMudar={() => {
          troca.reset()
          setRemovida(false)
        }}
        aoEnviar={(conta) => {
          troca.reset()
          setRemovida(conta.senha === '')
          troca.mutate({ credencial: 'imap', conta })
        }}
      />
      {troca.isError ? (
        <p className="integracoes__erro" role="alert">
          {mensagemDe(troca.error)}
        </p>
      ) : null}
      {troca.isSuccess ? (
        <p className="integracoes__sucesso" role="status">
          {removida ? SENHA_REMOVIDA : CONTA_SALVA}
        </p>
      ) : null}
    </div>
  )
}

function FormularioDaContaImap({
  imap,
  salvando,
  aoMudar,
  aoEnviar,
}: {
  imap: CredenciaisDaComunicacao['imap']
  salvando: boolean
  aoMudar: () => void
  aoEnviar: (conta: ContaImap) => void
}) {
  const ehCelular = useEhCelular()
  const [servidor, setServidor] = useState(imap.servidor ?? '')
  const [porta, setPorta] = useState(String(imap.porta ?? 993))
  const [usuario, setUsuario] = useState(imap.usuario ?? '')
  const [senha, setSenha] = useState('')
  const [seguranca, setSeguranca] = useState<ContaImap['seguranca']>(imap.seguranca ?? 'ssl')
  const [pasta, setPasta] = useState(imap.pasta ?? 'INBOX')
  const [recusaLocal, setRecusaLocal] = useState<string | null>(null)
  const id = useId()

  /** A conta do formulário, ou `null` com a recusa local já na tela. */
  function contaDoFormulario(senhaEnviada: string | null): ContaImap | null {
    const numero = Number(porta.trim())
    if (servidor.trim() === '') {
      setRecusaLocal(FALTA_O_SERVIDOR)
      return null
    }
    if (!Number.isInteger(numero) || numero < 1 || numero > 65535) {
      setRecusaLocal(PORTA_INVALIDA)
      return null
    }
    setRecusaLocal(null)
    return {
      servidor: servidor.trim(),
      porta: numero,
      usuario: usuario.trim(),
      senha: senhaEnviada,
      seguranca,
      pasta: pasta.trim() || 'INBOX',
    }
  }

  function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    // Em branco mantém a senha atual: ela nunca volta do serviço para ser reenviada (`null`).
    const conta = contaDoFormulario(senha === '' ? null : senha)
    if (conta !== null) {
      aoEnviar(conta)
      setSenha('')
    }
  }

  function removerASenha() {
    // `""` e o pedido explicito de apagar a senha gravada; `null` a manteria (contrato do PUT).
    const conta = contaDoFormulario('')
    if (conta !== null) {
      aoEnviar(conta)
      setSenha('')
    }
  }

  const mudou = () => {
    setRecusaLocal(null)
    aoMudar()
  }

  return (
    <form className="integracoes__formulario" onSubmit={enviar} noValidate>
      <div className={`integracoes__campos${ehCelular ? ' integracoes__campos--empilhado' : ''}`}>
        <div className="integracoes__campo-com-rotulo">
          <label className="integracoes__rotulo" htmlFor={`${id}-servidor`}>
            Servidor
          </label>
          <input
            id={`${id}-servidor`}
            className="integracoes__campo"
            value={servidor}
            onChange={(evento) => {
              mudou()
              setServidor(evento.target.value)
            }}
          />
        </div>
        <div className="integracoes__campo-com-rotulo">
          <label className="integracoes__rotulo" htmlFor={`${id}-porta`}>
            Porta
          </label>
          <input
            id={`${id}-porta`}
            className="integracoes__campo numero"
            inputMode="numeric"
            value={porta}
            onChange={(evento) => {
              mudou()
              setPorta(evento.target.value)
            }}
          />
        </div>
        <div className="integracoes__campo-com-rotulo">
          <label className="integracoes__rotulo" htmlFor={`${id}-usuario`}>
            Usuário
          </label>
          <input
            id={`${id}-usuario`}
            className="integracoes__campo"
            autoComplete="off"
            value={usuario}
            onChange={(evento) => {
              mudou()
              setUsuario(evento.target.value)
            }}
          />
        </div>
        <div className="integracoes__campo-com-rotulo">
          <label className="integracoes__rotulo" htmlFor={`${id}-senha`}>
            Nova senha
          </label>
          <input
            id={`${id}-senha`}
            className="integracoes__campo"
            type="password"
            autoComplete="new-password"
            placeholder="Em branco mantém a atual"
            value={senha}
            onChange={(evento) => {
              mudou()
              setSenha(evento.target.value)
            }}
          />
        </div>
        <div className="integracoes__campo-com-rotulo">
          <label className="integracoes__rotulo" htmlFor={`${id}-seguranca`}>
            Segurança
          </label>
          <select
            id={`${id}-seguranca`}
            className="integracoes__campo"
            value={seguranca}
            onChange={(evento) => {
              mudou()
              setSeguranca(evento.target.value as ContaImap['seguranca'])
            }}
          >
            {SEGURANCAS.map((opcao) => (
              <option key={opcao.valor} value={opcao.valor}>
                {opcao.rotulo}
              </option>
            ))}
          </select>
        </div>
        <div className="integracoes__campo-com-rotulo">
          <label className="integracoes__rotulo" htmlFor={`${id}-pasta`}>
            Pasta
          </label>
          <input
            id={`${id}-pasta`}
            className="integracoes__campo"
            value={pasta}
            onChange={(evento) => {
              mudou()
              setPasta(evento.target.value)
            }}
          />
        </div>
      </div>
      <div className="integracoes__acoes">
        <button className="integracoes__botao" type="submit" disabled={salvando}>
          {salvando ? 'Salvando…' : 'Salvar conta IMAP'}
        </button>
        {imap.senha.configurada ? (
          <button
            className="integracoes__botao integracoes__botao--secundario"
            type="button"
            disabled={salvando}
            onClick={removerASenha}
          >
            {REMOVER_A_SENHA}
          </button>
        ) : null}
      </div>
      {recusaLocal === null ? null : (
        <p className="integracoes__erro" role="alert">
          {recusaLocal}
        </p>
      )}
    </form>
  )
}

function SmtpNaTela({ credenciais }: { credenciais: CredenciaisDaComunicacao }) {
  const smtp = credenciais.smtp
  return (
    <div className="integracoes__bloco">
      <h4 className="integracoes__subtitulo">SMTP (envio)</h4>
      <p className="integracoes__estado">
        <span
          className="integracoes__selo"
          data-situacao={smtp.configurado ? 'configurada' : 'ausente'}
        >
          {smtp.configurado ? 'Configurado' : 'Não configurado'}
        </span>
        {smtp.servidor ? ` · servidor ${smtp.servidor}` : null}
        {smtp.remetente ? ` · remetente ${smtp.remetente}` : null}
      </p>
      <p className="integracoes__ajuda">
        Só leitura: a conta SMTP é a mesma dos e-mails de redefinição de senha e muda apenas no
        ambiente do servidor.
      </p>
    </div>
  )
}

/** Separa a lista digitada — uma por linha ou por vírgula — sem vazios. */
export function separarDestinatarios(texto: string): string[] {
  return texto
    .split(/[\n,;]/)
    .map((parte) => parte.trim())
    .filter((parte) => parte !== '')
}

function DestinatariosNaTela({ credenciais }: { credenciais: CredenciaisDaComunicacao }) {
  const atuais = credenciais.destinatarios_do_aviso_de_rat
  const troca = useTrocarCredencialDaComunicacao()

  return (
    <div className="integracoes__bloco">
      <h4 className="integracoes__subtitulo">Destinatários do aviso de RAT</h4>
      <p className="integracoes__ajuda">
        Não são usuários do CRM.
        {atuais.atualizada_em ? (
          <>
            {' Atualizado em '}
            <span className="numero">{dataBrasileira(atuais.atualizada_em)}</span>.
          </>
        ) : null}
      </p>
      {/* Remonta quando a lista do servidor muda — o rascunho velho não fica por cima dela. */}
      <FormularioDosDestinatarios
        key={atuais.atualizada_em ?? 'sem-lista'}
        destinatarios={atuais.destinatarios}
        salvando={troca.isPending}
        aoMudar={() => troca.reset()}
        aoEnviar={(destinatarios) => {
          troca.reset()
          troca.mutate({ credencial: 'destinatarios', destinatarios })
        }}
      />
      {troca.isError ? (
        <p className="integracoes__erro" role="alert">
          {mensagemDe(troca.error)}
        </p>
      ) : null}
      {troca.isSuccess ? (
        <p className="integracoes__sucesso" role="status">
          {DESTINATARIOS_SALVOS}
        </p>
      ) : null}
    </div>
  )
}

function FormularioDosDestinatarios({
  destinatarios,
  salvando,
  aoMudar,
  aoEnviar,
}: {
  destinatarios: readonly string[]
  salvando: boolean
  aoMudar: () => void
  aoEnviar: (destinatarios: string[]) => void
}) {
  const [texto, setTexto] = useState(destinatarios.join('\n'))
  const [recusaLocal, setRecusaLocal] = useState<string | null>(null)
  const id = useId()

  function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    const lista = separarDestinatarios(texto)
    if (lista.length === 0) {
      setRecusaLocal(FALTA_DESTINATARIO)
      return
    }
    setRecusaLocal(null)
    aoEnviar(lista)
  }

  return (
    <form className="integracoes__formulario" onSubmit={enviar} noValidate>
      <label className="integracoes__rotulo" htmlFor={id}>
        Um e-mail por linha
      </label>
      <textarea
        id={id}
        className="integracoes__campo integracoes__campo--texto-longo"
        value={texto}
        onChange={(evento) => {
          setRecusaLocal(null)
          aoMudar()
          setTexto(evento.target.value)
        }}
      />
      <div className="integracoes__acoes">
        <button className="integracoes__botao" type="submit" disabled={salvando}>
          {salvando ? 'Salvando…' : 'Salvar destinatários'}
        </button>
      </div>
      {recusaLocal === null ? null : (
        <p className="integracoes__erro" role="alert">
          {recusaLocal}
        </p>
      )}
    </form>
  )
}

/** Os dois cartões da aba E-mail, com os nomes do protótipo v2. */
export const TITULO_DO_ARQUIVAMENTO = 'E-mail arquivado por cópia'
export const TITULO_DO_AVISO_DE_RAT = 'Aviso de RAT ao pós-venda'

/** A aba E-mail: a conta IMAP com o SMTP derivado, e os destinatários do aviso de RAT, lado a lado. */
export function AbaDeEmail() {
  const podeEditar = usePodeEditarCredenciais()
  const consulta = useIntegracoesDaComunicacao({ habilitada: podeEditar })
  if (!podeEditar) {
    return null
  }
  return (
    <Grade>
      <Cartao
        titulo={TITULO_DO_ARQUIVAMENTO}
        ajuda="Copie a caixa IMAP nos e-mails com o cliente e eles entram no histórico do negócio. O SMTP é o que envia."
      >
        <ConteudoDasCredenciais consulta={consulta}>
          {(credenciais: CredenciaisDaComunicacao) => (
            <>
              <ContaImapNaTela credenciais={credenciais} />
              <SmtpNaTela credenciais={credenciais} />
            </>
          )}
        </ConteudoDasCredenciais>
      </Cartao>
      <Cartao
        titulo={TITULO_DO_AVISO_DE_RAT}
        ajuda="Quem recebe e-mail quando uma RAT é aberta. Sem destinatário, nenhuma RAT nova avisa ninguém."
      >
        <ConteudoDasCredenciais consulta={consulta}>
          {(credenciais: CredenciaisDaComunicacao) => (
            <DestinatariosNaTela credenciais={credenciais} />
          )}
        </ConteudoDasCredenciais>
      </Cartao>
    </Grade>
  )
}

/* --- A cadeia erro → carregando → sem bloco → conteúdo --------------------------------- */

interface ConsultaDeCredenciais<T> {
  data: { credenciais?: T | null } | undefined
  isError: boolean
  isLoading: boolean
  error: unknown
  refetch: () => unknown
}

function ConteudoDasCredenciais<T>({
  consulta,
  children,
}: {
  consulta: ConsultaDeCredenciais<T>
  children: (credenciais: T) => ReactNode
}) {
  if (consulta.isError) {
    return (
      <ErroDaLeitura
        erro={consulta.error}
        aoTentar={() => {
          void consulta.refetch()
        }}
      />
    )
  }
  if (consulta.data === undefined) {
    return (
      <p className="integracoes__ajuda" role="status">
        {CARREGANDO_AS_CREDENCIAIS}
      </p>
    )
  }
  const credenciais = consulta.data.credenciais
  if (credenciais === undefined || credenciais === null) {
    return <p className="integracoes__ajuda">{SEM_CREDENCIAIS_PARA_ESTE_PERFIL}</p>
  }
  return <>{children(credenciais)}</>
}
